import { create } from 'zustand'
import {DatasetType, MarkerStore} from "../types/marker";

export const useMarkerStore = create<MarkerStore>((set) => ({
  markers: [],
  activeMarker: null,
  isAddMarkerMode: false,

  addMarker: (marker) =>
    set((state) => ({
      markers: [...state.markers.map((m) => ({ ...m, active: false })), marker],
      activeMarker: marker,
    })),
  restoreMarkers: (markers) =>
    set(() => ({
      markers: markers,
      activeMarker: markers.find((m) => m.active),
    })),
  deleteMarker: (markerId) =>
    set((state) => {
      const updatedMarkers = state.markers.filter((marker) => marker.id !== markerId);
      const newActiveMarker = state.activeMarker?.id === markerId
        ? null
        : state.activeMarker;
      return {
        markers: updatedMarkers,
        activeMarker: newActiveMarker,
      };
    }),
  updateMarker: (markerId, updates) =>
    set((state) => ({
      markers: state.markers.map((m) => (m.id === markerId ? { ...m, ...updates } : m)),
      activeMarker:
        state.activeMarker?.id === markerId
          ? { ...state.activeMarker, ...updates }
          : state.activeMarker,
    })),
  setActiveMarker: (markerId) =>
    set((state) => {
      const updatedMarkers = state.markers.map((marker) => ({
        ...marker,
        active: marker.id === markerId
      }));

      const activeMarker = updatedMarkers.find((m) => m.id === markerId) || null;

      return {
        markers: updatedMarkers,
        activeMarker,
      };
    }),
  setAddMarkerMode: (status) => set({ isAddMarkerMode: status }),
  setActiveKey: (markerId, metadataType) =>
    set((state) => ({
      markers: state.markers.map((marker) =>
        marker.id === markerId ? { ...marker, key: metadataType } : marker
      ),
      activeMarker:
        state.activeMarker?.id === markerId
          ? { ...state.activeMarker, key: metadataType }
          : state.activeMarker,
    })),

  setResetMarker: () => set(() => ({
    markers: [],
    activeMarker: null,
  })),

  setDownloadPage: (markerId, panelName, value) =>
    set((state) => {
      const updatedMarkers = state.markers.map((marker) => {
        if (marker.id !== markerId) return marker;

        const currentDownloadPages = Array.isArray(marker.downloadPage)
          ? [...marker.downloadPage]
          : [];

        const existingIndex = currentDownloadPages.findIndex(
          item => item && item.radioKey === panelName
        );

        let newDownloadPages;

        if (existingIndex >= 0) {
          newDownloadPages = [...currentDownloadPages];
          newDownloadPages[existingIndex] = value;
        } else {
          newDownloadPages = [...currentDownloadPages, value];
        }

        return {
          ...marker,
          downloadPage: newDownloadPages
        };
      });

      const updatedActiveMarker = state.activeMarker?.id === markerId
        ? updatedMarkers.find(m => m.id === markerId) || state.activeMarker
        : state.activeMarker;

      return {
        markers: updatedMarkers,
        activeMarker: updatedActiveMarker
      };
    }),

  setWaterDepth: (markerId: string, panelName: string, value: string | number | null) => {
    set(state => {
      const markerToUpdate = state.markers.find(marker => marker.id === markerId);
      if (!markerToUpdate) return state;

      const currentValues = markerToUpdate.waterDepth || {};
      const newValue = value === null ? '-' : value;

      const updatedDepthData = {
        ...currentValues,
        [panelName]: newValue
      };

      return {
        markers: state.markers.map(marker =>
          marker.id === markerId
            ? { ...marker, waterDepth: updatedDepthData }
            : marker
        ),
        activeMarker: state.activeMarker?.id === markerId
          ? { ...state.activeMarker, waterDepth: updatedDepthData }
          : state.activeMarker
      };
    });
  },

  setCellSize: (markerId: string, panelName: string, value: string | number | null) => {
    set(state => {
      const markerToUpdate = state.markers.find(marker => marker.id === markerId);
      if (!markerToUpdate) return state;

      const currentValues = markerToUpdate.cellSize || {};
      const newValue = value === null ? '-' : value;

      const updatedCellSizeData = {
        ...currentValues,
        [panelName]: newValue
      };

      return {
        markers: state.markers.map(marker =>
          marker.id === markerId
            ? { ...marker, cellSize: updatedCellSizeData }
            : marker
        ),
        activeMarker: state.activeMarker?.id === markerId
          ? { ...state.activeMarker, cellSize: updatedCellSizeData }
          : state.activeMarker
      };
    });
  },

  setDatasetOffline: (markerId: string, panelName: string, value: boolean) => {
    set(state => {
      const markerToUpdate = state.markers.find(marker => marker.id === markerId);
      if (!markerToUpdate) return state;

      const currentValues = markerToUpdate.isDatasetOffline || {};

      const updatedOfflineData = {
        ...currentValues,
        [panelName]: value
      };

      return {
        markers: state.markers.map(marker =>
          marker.id === markerId
            ? { ...marker, isDatasetOffline: updatedOfflineData }
            : marker
        ),
        activeMarker: state.activeMarker?.id === markerId
          ? { ...state.activeMarker, isDatasetOffline: updatedOfflineData }
          : state.activeMarker
      };
    });
  },

  setDatasetReport: (markerId: string, panelName: string, value: boolean) => {
    set(state => {
      const markerToUpdate = state.markers.find(marker => marker.id === markerId);
      if (!markerToUpdate) return state;

      const currentValues = markerToUpdate.isDatasetHasReport || {};

      const updatedReportData = {
        ...currentValues,
        [panelName]: value
      };

      return {
        markers: state.markers.map(marker =>
          marker.id === markerId
            ? { ...marker, isDatasetHasReport: updatedReportData }
            : marker
        ),
        activeMarker: state.activeMarker?.id === markerId
          ? { ...state.activeMarker, isDatasetHasReport: updatedReportData }
          : state.activeMarker
      };
    });
  },

  setActiveDatasetKey: (markerId: string, datasetKey: DatasetType, newActiveDataset: string) => {
    set(state => ({
      markers: state.markers.map(marker =>
        marker.id === markerId ? { ...marker, activeDatasetKey: datasetKey, dataset: newActiveDataset } : marker
      ),
      activeMarker: state.activeMarker?.id === markerId
        ? { ...state.activeMarker, activeDatasetKey: datasetKey, dataset: newActiveDataset }
        : state.activeMarker
    }));
  },
}))
